import React, { createContext, useContext, ReactNode } from "react";
import { useProfileState } from "@/hooks/useProfileState";
import type { UserProfile } from "@/types/profile";

interface ProfileContextType {
  profile: UserProfile;
  /** Save a delta of changed profile fields. One profiles.update + per-field activity log + optional snapshot. */
  saveProfileDelta: (
    delta: Partial<UserProfile>,
    previousValues?: Partial<UserProfile>,
  ) => Promise<void>;
  /** Single-field update — kept for the profile image flow (save-on-upload). */
  updateProfile: (field: keyof UserProfile, value: any) => Promise<void>;
  resetProfile: () => void;
  isLoading: boolean;
  isSaving: boolean;
  error: Error | null;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const profileState = useProfileState();

  return (
    <ProfileContext.Provider value={profileState as ProfileContextType}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
}

export function useUserProfile() {
  const { profile } = useProfile();

  const displayName = profile?.preferred_first_name || profile?.legal_first_name || 'User';
  const fullName = profile ? `${profile.legal_first_name || ''} ${profile.legal_last_name || ''}`.trim() : '';
  const email = profile?.business_email || '';

  return {
    displayName,
    fullName,
    email,
    profile
  };
}
